import type { MercureEvent, EventCallback, UnsubscribeFunction } from './types'

export class EventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map()
  private history: MercureEvent[] = []
  private maxHistorySize = 50

  emit(event: MercureEvent): void {
    // Add to history
    this.addToHistory(event)

    // Notify specific event listeners
    const listeners = this.listeners.get(event.type)
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(event)
        } catch (error) {
          console.error(`Error in event listener for ${event.type}:`, error)
        }
      })
    }

    // Notify wildcard listeners
    const wildcardListeners = this.listeners.get('*')
    if (wildcardListeners) {
      wildcardListeners.forEach((callback) => {
        try {
          callback(event)
        } catch (error) {
          console.error('Error in wildcard event listener:', error)
        }
      })
    }
  }

  on(eventType: string, callback: EventCallback): UnsubscribeFunction {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }

    this.listeners.get(eventType)!.add(callback)

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  off(eventType: string, callback?: EventCallback): void {
    if (!callback) {
      // Remove all listeners for this event type
      this.listeners.delete(eventType)
      return
    }

    const listeners = this.listeners.get(eventType)
    if (listeners) {
      listeners.delete(callback)
      if (listeners.size === 0) {
        this.listeners.delete(eventType)
      }
    }
  }

  once(eventType: string, callback: EventCallback): UnsubscribeFunction {
    const wrappedCallback = (event: MercureEvent) => {
      callback(event)
      this.off(eventType, wrappedCallback)
    }

    return this.on(eventType, wrappedCallback)
  }

  getHistory(eventType?: string): MercureEvent[] {
    if (!eventType) {
      return [...this.history]
    }

    return this.history.filter((event) => event.type === eventType)
  }

  clearHistory(): void {
    this.history = []
  }

  getListenerCount(eventType?: string): number {
    if (!eventType) {
      let total = 0
      this.listeners.forEach((listeners) => {
        total += listeners.size
      })
      return total
    }

    const listeners = this.listeners.get(eventType)
    return listeners ? listeners.size : 0
  }

  getEventTypes(): string[] {
    return Array.from(this.listeners.keys())
  }

  private addToHistory(event: MercureEvent): void {
    this.history.unshift(event)

    // Keep only the most recent events
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(0, this.maxHistorySize)
    }
  }

  // Debug method to inspect current state
  debug(): void {
    console.group('EventBus Debug Info')
    console.log(
      'Active listeners:',
      Object.fromEntries(
        Array.from(this.listeners.entries()).map(([type, listeners]) => [type, listeners.size]),
      ),
    )
    console.log('History size:', this.history.length)
    console.log(
      'Recent events:',
      this.history.slice(0, 5).map((e) => ({ type: e.type, timestamp: e.timestamp })),
    )
    console.groupEnd()
  }
}
